import { inject, Injectable } from '@angular/core';
import { LimitedCurrencyAmount } from '@lx/currency/shared/model/currency.model';
import { formatDate, YesNo } from '@lx/quote-tool/shared/google-analytics';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { QuoteStore, QuoteToolLiteStore } from '../store';
import { GoogleAnalyticsParametersType, GoogleAnalyticsQuoteToolLiteQuote } from './google-analytics-parameters.type';
import { QuoteFieldsBuilders } from './quote-data-layer.strategy';

/**
 * Service to provide quote-related data for Google Analytics dataLayer object across flows.
 */
@Injectable({ providedIn: 'root' })
export class QuoteDataLayerService {
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly quoteStore = inject(QuoteStore);

  /**
   * Builds the quote data layer object for Google Analytics. It uses the provided builders for each step to construct the quote data.
   *
   * @param quoteFieldsBuilders - A partial record mapping RouteIds to functions that return observables of partial GoogleAnalyticsQuote objects. Those
   * functions are used to build the quote data for each step in the quote process. If not provided or empty, an observable emitting an empty object is returned.
   *
   * @returns An observable that emits a partial GoogleAnalyticsParametersType object containing the quote data.
   */
  buildQuoteDataLayerObject(quoteFieldsBuilders: QuoteFieldsBuilders): Observable<Partial<GoogleAnalyticsParametersType>> {
    if (!quoteFieldsBuilders || Object.keys(quoteFieldsBuilders).length === 0) {
      return of({});
    }

    return this.quoteToolLiteStore.currentStepId$.pipe(
      switchMap((currentStepId) => {
        const stepBuilder = quoteFieldsBuilders[currentStepId];
        return stepBuilder ? stepBuilder() : of({});
      }),
      map((quoteData) => ({
        quotes: [quoteData],
      })),
    );
  }

  /**
   * Gets the base quote data for Google Analytics dataLayer object across all flows.
   */
  getBaseData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return this.quoteToolLiteStore.baseSelectionForDataLayer$.pipe(
      map(({ quote, programType, course, duration, startDate, endDate }) => ({
        quote,
        quote_program_type: programType,
        quote_course_detail: course,
        quote_duration_interest: duration,
        quote_start_date: formatDate(startDate),
        quote_end_date: formatDate(endDate),
      })),
    );
  }

  /**
   * Gets the quote prices related data for Google Analytics dataLayer object.
   */
  getPricesData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return this.quoteStore.pricesSelectionForDataLayer$.pipe(
      map(({ productTotal, grandTotal, destinationCurrency, displayCurrency, discountTotalAsPercentage, discountTotal }) => ({
        quote_promo_applied: discountTotalAsPercentage ? YesNo.Yes : YesNo.No,
        quote_price_discounted: this.getPriceInCurrency(grandTotal, displayCurrency),
        quote_local_currency: displayCurrency,
        quote_destination_currency: destinationCurrency,
        quote_total_amount: this.getPriceInCurrency(grandTotal, displayCurrency),
        quote_price_original: this.getPriceInCurrency(productTotal, displayCurrency),
        quote_discount_amount: this.getPriceInCurrency(discountTotal, displayCurrency),
      })),
    );
  }

  getInsuranceData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return combineLatest([this.quoteStore.insurance$, this.quoteToolLiteStore.selectedDuration$]).pipe(
      map(([insurance, duration]) => {
        if (!insurance) {
          return {
            quote_insurance_type: YesNo.No,
          };
        }

        return {
          quote_insurance_type: YesNo.Yes,
          quote_insurance_weeks: duration,
        };
      }),
    );
  }

  getTransfersData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return this.quoteStore.transfers$.pipe(
      map((transfers) => {
        if (!transfers || !transfers.length) {
          return {
            quote_transfer_type: YesNo.No,
          };
        }

        return {
          quote_transfer_type: YesNo.Yes,
          quote_transfer_type_name: transfers.map(({ name }) => name).join(', '),
        };
      }),
    );
  }

  getProfessionalImmersionExperienceData(): Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>> {
    return combineLatest([this.quoteStore.professionalImmersionExperience$, this.quoteToolLiteStore.selectedDuration$]).pipe(
      map(([professionalImmersionExperience, duration]) => {
        if (!professionalImmersionExperience) {
          return {
            quote_professional_immersion_experience_type: YesNo.No,
          };
        }

        return {
          quote_professional_immersion_experience_type: YesNo.Yes,
          quote_professional_immersion_experience_weeks: duration,
        };
      }),
    );
  }

  private getPriceInCurrency(price: LimitedCurrencyAmount[], currency: string): string | undefined {
    if (!price || !price.length) {
      return undefined;
    }
    const priceInCurrency = price.find((p) => p.currency === currency);
    return priceInCurrency ? Math.round(priceInCurrency.value).toString() : undefined;
  }
}
